import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, ShieldCheckIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ArrowPathIcon, PencilIcon,
} from '@heroicons/react/24/outline';
import { apiGet, apiPost } from '../../../utils/api';

const VERIFICATION_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string; description: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-600', icon: <PencilIcon className="w-5 h-5" />, label: 'Draft', description: 'Complete your profile and submit for verification.' },
  submitted: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <ArrowPathIcon className="w-5 h-5" />, label: 'Submitted', description: 'Your profile is being reviewed by our team.' },
  under_review: { bg: 'bg-amber-100', text: 'text-amber-700', icon: <ArrowPathIcon className="w-5 h-5 animate-spin" />, label: 'Under Review', description: 'Our team is actively reviewing your application.' },
  verified: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircleIcon className="w-5 h-5" />, label: 'Verified', description: 'Your supplier account is fully verified and active.' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: <ExclamationTriangleIcon className="w-5 h-5" />, label: 'Rejected', description: 'Please review the feedback and update your profile.' },
  suspended: { bg: 'bg-red-100', text: 'text-red-700', icon: <ExclamationTriangleIcon className="w-5 h-5" />, label: 'Suspended', description: 'Your account has been suspended. Contact support.' },
};

const SupplierProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    business_name: '', business_type: 'agricultural_supplier',
    contact_email: '', contact_phone: '', contact_person: '',
    address: '', city: '', country: 'Rwanda', tax_id: '', logo_url: '',
  });
  const [products, setProducts] = useState<string[]>(['']);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGet<any>('/api/marketplace/b2b/supplier/profile');
        if (data.profile) {
            setProfile(data.profile);
            setForm({
              business_name: data.profile.business_name || '',
              business_type: data.profile.business_type || 'agricultural_supplier',
              contact_email: data.profile.contact_email || '',
              contact_phone: data.profile.contact_phone || '',
              contact_person: data.profile.contact_person || '',
              address: data.profile.address || '',
              city: data.profile.city || '',
              country: data.profile.country || 'Rwanda',
              tax_id: data.profile.tax_id || '',
              logo_url: data.profile.logo_url || '',
            });
            const prods = data.profile.products;
            if (Array.isArray(prods) && prods.length > 0) {
              setProducts(prods);
            }
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const addProduct = () => setProducts([...products, '']);
  const removeProduct = (i: number) => setProducts(products.filter((_, idx) => idx !== i));
  const updateProduct = (i: number, val: string) => {
    const next = [...products];
    next[i] = val;
    setProducts(next);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const data = await apiPost<any>('/api/marketplace/b2b/supplier/profile', {
        ...form,
        products: products.filter(p => p.trim()),
      });
      setProfile(data.profile);
      setMessage('Profile saved successfully.');
    } catch (err: any) {
      setMessage(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForVerification = async () => {
    setSubmitting(true);
    setMessage('');
    try {
      const data = await apiPost<any>('/api/marketplace/b2b/supplier/profile/submit', {});
      setProfile(data.profile);
      setMessage('Profile submitted for verification!');
    } catch (err: any) {
      setMessage(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const status = profile?.verification_status || 'draft';
  const vStyle = VERIFICATION_STYLES[status] || VERIFICATION_STYLES.draft;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/marketplace/supplier')} className="p-2 rounded-lg hover:bg-gray-200 transition">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Supplier Profile</h1>
            <p className="text-sm text-gray-500">Manage your business profile and verification status</p>
          </div>
        </div>

        {/* Registration Number */}
        {profile?.registration_number && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">Registration Number</p>
              <p className="text-lg font-bold text-gray-900 font-mono">{profile.registration_number}</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${vStyle.bg} ${vStyle.text}`}>
              {vStyle.label}
            </div>
          </div>
        )}

        {/* Verification Status Banner */}
        <div className={`rounded-xl p-4 mb-6 ${vStyle.bg}`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 ${vStyle.text}`}>{vStyle.icon}</div>
            <div>
              <h3 className={`text-sm font-semibold ${vStyle.text}`}>Verification: {vStyle.label}</h3>
              <p className="text-xs text-gray-600 mt-1">{vStyle.description}</p>
              {profile?.verification_reason && status === 'rejected' && (
                <p className="text-xs text-red-600 mt-2 font-medium">Reason: {profile.verification_reason}</p>
              )}
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                <input type="text" value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                <select value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm">
                  <option value="agricultural_supplier">Agricultural Supplier</option>
                  <option value="input_dealer">Input Dealer</option>
                  <option value="equipment_supplier">Equipment Supplier</option>
                  <option value="logistics_provider">Logistics Provider</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                <input type="text" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input type="text" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="text" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
              </div>
            </div>

            {/* Products */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Products *</label>
              <p className="text-xs text-gray-500 mb-2">List the products you sell on the marketplace</p>
              <div className="space-y-2">
                {products.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input type="text" value={p} onChange={e => updateProduct(i, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                      placeholder={`Product ${i + 1}`} />
                    {products.length > 1 && (
                      <button type="button" onClick={() => removeProduct(i)}
                        className="px-2 py-2 text-red-500 hover:text-red-700 text-sm">Remove</button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addProduct}
                className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium">+ Add another product</button>
            </div>

            {message && (
              <p className={`text-sm rounded-lg px-3 py-2 ${message.includes('success') || message.includes('saved') || message.includes('submitted') ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                {message}
              </p>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition">
                {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              {(status === 'draft' || status === 'rejected') && (
                <button onClick={handleSubmitForVerification} disabled={submitting || !form.business_name}
                  className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition">
                  {submitting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <ShieldCheckIcon className="w-4 h-4" />}
                  {submitting ? 'Submitting...' : 'Submit for Verification'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierProfilePage;
